/**
 * Patient Data Adapter
 *
 * This adapter normalizes patient data from different sources (Demo, FHIR/Greenfield)
 * into a consistent structure for use throughout the application.
 */

/**
 * Normalizes patient data regardless of source (Demo or FHIR/Greenfield)
 * @param {Object} patient - Raw patient data from any source
 * @param {string} source - Data source ('demo' or 'greenfield')
 * @returns {Object} Normalized patient data with consistent structure
 */
export function normalizePatientData(patient, source = 'demo') {
  if (!patient) return null;

  // Clone to avoid mutations
  const normalizedPatient = { ...patient };

  // Add source identifier if not present
  normalizedPatient._source = normalizedPatient._source || source;

  // Normalize allergies
  normalizedPatient.allergies = getNormalizedAllergies(patient);

  // Normalize medications
  normalizedPatient.medications = getNormalizedMedications(patient);

  // Normalize comorbidities/conditions
  normalizedPatient.comorbidities = getNormalizedComorbidities(patient);

  // Normalize labs
  normalizedPatient.labs = getNormalizedLabs(patient);

  // Handle surgery/surgeries data
  if (patient.surgery) {
    // Handle JSON string format for surgery
    if (typeof patient.surgery === 'string') {
      try {
        normalizedPatient.surgery = JSON.parse(patient.surgery);
      } catch (e) {
        console.warn('Failed to parse surgery JSON string:', e);
        normalizedPatient.surgery = patient.surgery;
      }
    } else {
      normalizedPatient.surgery = patient.surgery;
    }
  }
  if (patient.surgeries) {
    // Handle JSON string format for surgeries
    if (typeof patient.surgeries === 'string') {
      try {
        normalizedPatient.surgeries = JSON.parse(patient.surgeries);
      } catch (e) {
        console.warn('Failed to parse surgeries JSON string:', e);
        normalizedPatient.surgeries = patient.surgeries;
      }
    } else {
      normalizedPatient.surgeries = patient.surgeries;
    }
  }

  // Preserve surgeon information
  if (patient.surgeonRef) {
    normalizedPatient.surgeonRef = patient.surgeonRef;
  }
  if (patient.surgeon) {
    // Handle surgeon field which could be:
    // 1. A plain string (e.g., "Dr. Jane Smith")
    // 2. A JSON string representing an object
    // 3. An object
    if (typeof patient.surgeon === 'string') {
      // Check if it's a JSON string by looking for object/array markers
      const trimmed = patient.surgeon.trim();
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          normalizedPatient.surgeon = JSON.parse(patient.surgeon);
        } catch (e) {
          console.warn('Failed to parse surgeon JSON string:', e);
          // Keep as string if JSON parsing fails
          normalizedPatient.surgeon = patient.surgeon;
        }
      } else {
        // It's a plain string (surgeon name), keep it as is
        normalizedPatient.surgeon = patient.surgeon;
      }
    } else {
      normalizedPatient.surgeon = patient.surgeon;
    }
  }
  if (patient.surgeonId) {
    normalizedPatient.surgeonId = patient.surgeonId;
  }
  if (patient.surgeonName) {
    normalizedPatient.surgeonName = patient.surgeonName;
  }

  return normalizedPatient;
}

/**
 * Extracts and normalizes allergies from any patient data structure
 */
function getNormalizedAllergies(patient) {
  if (!patient) return [];

  // Handle JSON string format (from API)
  if (typeof patient.allergies === 'string') {
    try {
      const parsed = JSON.parse(patient.allergies);
      if (Array.isArray(parsed)) {
        return parsed.map(allergy => {
          if (typeof allergy === 'string') return { name: allergy };
          return allergy;
        });
      }
    } catch (e) {
      console.warn('Failed to parse allergies JSON string:', e);
      return [];
    }
  }

  // Demo format: patient.allergies array of {name: string} objects
  if (Array.isArray(patient.allergies) && patient.allergies.length > 0) {
    return patient.allergies.map(allergy => {
      if (typeof allergy === 'string') return { name: allergy };
      return allergy;
    });
  }

  // FHIR format: patient.allergyIntolerance array
  if (Array.isArray(patient.allergyIntolerance) && patient.allergyIntolerance.length > 0) {
    return patient.allergyIntolerance.map(allergy => ({
      name: allergy.display || allergy.code?.text || 'Unknown Allergy',
      severity: allergy.severity || 'unknown',
      status: allergy.status || 'active',
      // Store original FHIR data for reference if needed
      _fhirData: allergy,
    }));
  }

  return [];
}

/**
 * Extracts and normalizes medications from any patient data structure
 */
function getNormalizedMedications(patient) {
  if (!patient) return [];

  // Handle JSON string format (from API)
  if (typeof patient.medications === 'string') {
    try {
      const parsed = JSON.parse(patient.medications);
      if (Array.isArray(parsed)) {
        return parsed.map(med => {
          if (typeof med === 'string') return { name: med };
          return med;
        });
      }
    } catch (e) {
      console.warn('Failed to parse medications JSON string:', e);
      return [];
    }
  }

  // Demo format: patient.medications array of strings
  if (Array.isArray(patient.medications) && patient.medications.length > 0) {
    return patient.medications.map(med => {
      if (typeof med === 'string') return { name: med };
      return med;
    });
  }

  // FHIR format: patient.medicationStatement array
  if (Array.isArray(patient.medicationStatement) && patient.medicationStatement.length > 0) {
    return patient.medicationStatement.map(med => ({
      name:
        med.display ||
        med.code?.text ||
        med.medicationCodeableConcept?.text ||
        'Unknown Medication',
      dosage: med.dosage?.[0]?.text || '',
      status: med.status || 'active',
      // Store original FHIR data for reference if needed
      _fhirData: med,
    }));
  }

  return [];
}

/**
 * Extracts and normalizes comorbidities/conditions from any patient data structure
 */
function getNormalizedComorbidities(patient) {
  if (!patient) return [];

  // Handle JSON string format (from API)
  if (typeof patient.comorbidities === 'string') {
    try {
      const parsed = JSON.parse(patient.comorbidities);
      if (Array.isArray(parsed)) {
        return parsed.map(condition => {
          if (typeof condition === 'string') return { name: condition };
          return condition;
        });
      }
    } catch (e) {
      console.warn('Failed to parse comorbidities JSON string:', e);
      return [];
    }
  }

  // Demo format: patient.comorbidities array of strings
  if (Array.isArray(patient.comorbidities) && patient.comorbidities.length > 0) {
    return patient.comorbidities.map(condition => {
      if (typeof condition === 'string') return { name: condition };
      return condition;
    });
  }

  // FHIR format: patient.conditions array
  if (Array.isArray(patient.conditions) && patient.conditions.length > 0) {
    return patient.conditions.map(condition => ({
      name: condition.display || condition.code?.text || 'Unknown Condition',
      status: condition.status || 'active',
      severity: condition.severity?.text || 'unknown',
      // Store original FHIR data for reference if needed
      _fhirData: condition,
    }));
  }

  return [];
}

/**
 * Extracts and normalizes lab results from any patient data structure
 */
function getNormalizedLabs(patient) {
  // Initialize normalized labs object
  const normalizedLabs = {};

  // Handle JSON string format (from API)
  if (typeof patient.labs === 'string') {
    try {
      const parsed = JSON.parse(patient.labs);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        // Labs as object (key-value pairs)
        Object.entries(parsed).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            normalizedLabs[key] = value;
          } else {
            normalizedLabs[key] = { value };
          }
        });
      } else if (Array.isArray(parsed)) {
        // Labs as array
        parsed.forEach(lab => {
          const key = lab.key || lab.name || lab.code;
          if (key) {
            normalizedLabs[key] = {
              value: lab.value,
              unit: lab.unit || lab.units || '',
              date: lab.date || new Date().toISOString(),
              status: lab.status || 'final',
            };
          }
        });
      }
    } catch (e) {
      console.warn('Failed to parse labs JSON string:', e);
    }
  }

  // Process patient.labs if available (demo format)
  else if (Array.isArray(patient.labs) && patient.labs.length > 0) {
    patient.labs.forEach(lab => {
      const key = lab.key || lab.name || lab.code;
      if (key) {
        normalizedLabs[key] = {
          value: lab.value,
          unit: lab.unit || lab.units || '',
          date: lab.date || new Date().toISOString(),
          status: lab.status || 'final',
        };
      }
    });
  }

  // Process summary.labs if available (processed FHIR format)
  if (patient.summary && patient.summary.labs) {
    Object.entries(patient.summary.labs).forEach(([key, value]) => {
      if (!normalizedLabs[key]) {
        normalizedLabs[key] = typeof value === 'object' ? value : { value };
      }
    });
  }

  // Process FHIR observations if available
  if (Array.isArray(patient.observations)) {
    patient.observations.forEach(obs => {
      // Extract lab code from FHIR observation
      const code = obs.code?.coding?.[0]?.code || obs.code?.text;
      if (code && !normalizedLabs[code]) {
        normalizedLabs[code] = {
          value: obs.valueQuantity?.value || obs.valueString || obs.valueInteger || null,
          unit: obs.valueQuantity?.unit || '',
          date: obs.effectiveDateTime || new Date().toISOString(),
          status: obs.status || 'final',
          // Store original FHIR data for reference if needed
          _fhirData: obs,
        };
      }
    });
  }

  return normalizedLabs;
}

/**
 * Normalizes a summary object with consistent structure
 */
export function normalizeSummary(summary, patient) {
  if (!summary) return { flags: [], labs: {} }; // Default labs to empty object

  const normalizedSummary = { ...summary };

  // Ensure flags are always an array
  if (!Array.isArray(normalizedSummary.flags)) {
    normalizedSummary.flags = [];
  }

  // Ensure labs exist, default to an empty object if not present or not an object
  if (typeof normalizedSummary.labs !== 'object' || normalizedSummary.labs === null) {
    normalizedSummary.labs = {};
  }

  // Handle JSON string format for labs in summary
  if (typeof normalizedSummary.labs === 'string') {
    try {
      normalizedSummary.labs = JSON.parse(normalizedSummary.labs);
    } catch (e) {
      console.warn('Failed to parse summary.labs JSON string:', e);
      normalizedSummary.labs = {};
    }
  }

  return normalizedSummary;
}
